import type { CarType } from "../types/CarType";
import type {
    CalculatePricesResultType,
    CalculateResultType,
    ExternalPriceCalculatorResultType,
    ExternalPriceCalulatedPriceType,
    ExternalPricePromoApplyType,
} from "../types/externalPriceTypes";
import type { PricePromotionType, PriceType } from "../../../common-deprecated/types/CommonTypes";
import {
    type PromotionalPrice,
    convertPromoToDiscountType,
    functions,
} from "../../../common-deprecated/dataFetching/calculateDiscountedPrices";
import type { BasketType } from "../types/BasketType";
import type { ItemsType } from "../../../common-deprecated/redux/typeHelpers";
import { showAemPriceExclVAT } from "../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import type { AemFmComponent } from "../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { ExternalConfigAdditionalInfo } from "../types/ExternalPriceType";

/**
 * is used to format the TMEF response of the prices method.
 * Only works on prices. Not price!
 * */
export const formatPricesResponse = (
    calculatedPrices: CalculatePricesResultType,
    modelCode: string,
    price: PriceType,
    commonSettings: CommonSettingsType,
    aemFmComponent: AemFmComponent,
): ExternalPriceCalulatedPriceType => {
    let extraCosts;

    const tax = calculatedPrices.AdditionalInfo.find((info) => info.Key === "tax")?.Value || 0;
    const totalValueWithoutTax =
        calculatedPrices.AdditionalInfo.find((info) => info.Key === "total-price-without-tax")?.Value || 0;

    const totalValue = calculatedPrices.Prices.reduce(
        (total, { Values }) =>
            total +
            (Values.find(({ Type }) => Type === getRSP(commonSettings, aemFmComponent, modelCode))?.Amount || 0),
        0,
    );

    if (calculatedPrices.ExtraCosts) {
        extraCosts = calculatedPrices.ExtraCosts.map((extraCost) => ({
            key: extraCost.Key,
            values: extraCost.Values.map((value) => ({
                amount: value.Amount,
                type: value.Type,
                currency: value.Currency,
            })),
            eposCode: extraCost.EposCode,
            eposVatCode: extraCost.EposVatCode,
            eposSelectionIndicator: extraCost.EposSelectionIndicator,
        }));
    }

    const carPrices = calculatedPrices.Prices.find(({ Type }) => Type === "CAR");
    const discount =
        carPrices?.Values.find(({ Type }) => Type === getRSP(commonSettings, aemFmComponent, modelCode))?.Amount || 0;
    const discounts = carPrices?.AppliedDiscounts?.reduce((total, { Amount }) => total + Amount, 0) || 0;
    const cash = discount + discounts;

    const carPrice = {
        ...price,
        cash,
        discount,
        onlineCashDiscount: discount,
        onlineMonthlyDiscount: discount,
    };
    return {
        totalValue: addExtraCostsToNetPricing(totalValue, extraCosts, commonSettings, aemFmComponent, modelCode),
        tax,
        carPrice,
        totalValueWithoutTax,
        extraCosts: extraCosts || [],
        calculatedRawData: { AdditionalInfo: calculatedPrices.AdditionalInfo },
        discounts,
    };
};

/**
 * Price Endpoint formatter
 * Still used in a lot of countries but switch to "prices" is coming
 * @deprecated
 * @param calculatedResults - Output from external price calculator
 * @param car
 * @param basket
 * @param items
 */
export const formatPriceResponse = (
    calculatedResults: CalculateResultType,
    car: CarType,
    basket: BasketType,
    items: ItemsType,
): ExternalPriceCalulatedPriceType => {
    const calculatedPrice = calculatedResults as CalculateResultType;
    const functionResults: ExternalPriceCalculatorResultType[] = calculatedPrice.result;
    // group results into correct structure for applying promotions

    const groupedFunctionResults = functionResults.reduce<{
        [key in ExternalPricePromoApplyType]: ExternalPriceCalculatorResultType[];
    }>(
        (acc, result) => {
            if (!acc[result.Type]) acc[result.Type] = [];
            acc[result.Type].push(result);
            return acc;
        },
        {} as { [key in ExternalPricePromoApplyType]: ExternalPriceCalculatorResultType[] },
    );

    // get all promotions
    const promotions: { [key in ExternalPricePromoApplyType]: PricePromotionType[] } = {
        CAR: car.price.promotions,
        COLOUR: basket.colour.price.promotions,
        UPHOLSTERY: basket.upholstery.price.promotions,
        WHEEL: basket.wheel.price.promotions,
        OPTIONS: basket.extras.map((extra) => (extra.type === "option" ? extra.price.promotions : [])).flat(),
        ACCESSORIES: basket.extras.map((extra) => (extra.type === "accessory" ? extra.price.promotions : [])).flat(),
        PACKS: basket.packs.map((pack) => pack.price.promotions).flat(),
        SERVICE: basket.serviceProducts.map((serviceProduct) => serviceProduct.price.promotions).flat(),
    };

    // Calculate promotions on each part
    const promoPrices = Object.entries(groupedFunctionResults).reduce(
        (acc: { [key in ExternalPricePromoApplyType]: PromotionalPrice[] } & { total: number }, [type, prices]) => {
            // create promo object for a part
            const promos = convertPromoToDiscountType(
                promotions[type as ExternalPricePromoApplyType],
                // This checks if the selected car has these promotions and overwrites the base promotion amount as the promotions can be overwritten on car level in carDB.
                items.promotions.map((promotion) => ({
                    ...promotion,
                    amount:
                        car.price.promotions.find((carPromotion) => carPromotion.id === promotion.id)?.amount ||
                        promotion.amount,
                })),
            );

            // calculate total price for each part
            // Make sure the instalationCost is included if it is winterWheels
            const calculatedPrices = prices.map((price) =>
                functions.calculateDiscountedPrices(
                    {
                        PriceInVat:
                            price.Type === "ACCESSORIES" &&
                            price.ID === basket.winterWheel?.id &&
                            basket.winterWheel.installTheWheels
                                ? price.CalculatedPrice + (basket.winterWheel.installationCost?.cash || 0)
                                : price.CalculatedPrice,
                    },
                    promos,
                ),
            );

            acc[type as ExternalPricePromoApplyType] = calculatedPrices;
            acc.total += calculatedPrices.reduce((accTotal, price) => accTotal + price.ListPriceWithDiscount, 0);

            return acc;
        },
        { total: 0 } as { [key in ExternalPricePromoApplyType]: PromotionalPrice[] } & { total: number },
    );

    // Return the calculated price and the tax
    const tax = calculatedPrice.tax || 0;
    const cash = groupedFunctionResults.CAR[0].CalculatedPrice;
    const discount = groupedFunctionResults.CAR[0].CalculatedPrice - promoPrices.CAR[0].ListPriceDiscount;
    const carPrice = {
        ...car.price,
        cash,
        discount,
        onlineCashDiscount: discount,
        onlineMonthlyDiscount: discount,
    };

    // Total value without tax is not supported on 'price'
    return {
        totalValue: promoPrices.total,
        tax,
        carPrice,
        totalValueWithoutTax: 0,
        extraCosts: [],
        calculatedRawData: { AdditionalInfo: calculatedPrice.AdditionalInfo },
    };
};

/**
 * Helper function in order to get "RSP NO VAT" or "RSP VAT".
 * Needed to know which prices result to show, i.e. incl. or excl. VAT.
 */
const getRSP = (
    commonSettings: CommonSettingsType,
    aemFmComponent: AemFmComponent,
    modelCode: string | undefined,
): string => {
    return showAemPriceExclVAT(commonSettings, aemFmComponent, "new", modelCode) ? "RSP NO VAT" : "RSP VAT";
};

export const extractAdditionalInfoFromExternalPrice = (
    externalPriceCalulatedPrice: ExternalPriceCalulatedPriceType,
): Record<ExternalConfigAdditionalInfo, number> | undefined => {
    const tmefAdditionalInfo = externalPriceCalulatedPrice.calculatedRawData.AdditionalInfo;
    // This section is specifically relevant only for the implementation scope of this method
    if (tmefAdditionalInfo && tmefAdditionalInfo.length) {
        const allowedKeys = Object.values(ExternalConfigAdditionalInfo);
        const parsedAdditionalInfo = tmefAdditionalInfo.reduce((acc: Record<string, number> | null, additionalInfo) => {
            const allowedKey = allowedKeys.find((key) => key.toLowerCase() === additionalInfo.Key.toLowerCase());
            if (allowedKey) {
                if (!acc) acc = {};
                acc[allowedKey] = additionalInfo.Value;
            }
            return acc;
        }, null);

        return parsedAdditionalInfo || undefined;
    }

    return undefined;
};

/**
 * In some cases the NMSC wants to add taxes to the netto price. This is the case for TNL to add BPM as this is a legal requirement
 * @param price
 * @param extraCosts - When external price is enabled TMEF will return an extraCosts object which will contain all applicable taxes.
 * @param commonSettings
 * @param aemFmComponent - ex. Carconfig, Build,...
 * @param modelCode
 */
const addExtraCostsToNetPricing = (
    price: number,
    extraCosts: ExternalPriceCalulatedPriceType["extraCosts"],
    commonSettings: CommonSettingsType,
    aemFmComponent: AemFmComponent,
    modelCode: string,
): number => {
    if (showAemPriceExclVAT(commonSettings, aemFmComponent, "new", modelCode)) {
        return (
            extraCosts?.reduce((totalPrice, costs) => {
                if (costs.key === "BPM") {
                    costs.values.forEach((value) => {
                        totalPrice += value.amount ?? 0;
                    });
                }
                return totalPrice;
            }, price) ?? price
        );
    } else return price;
};
